import { runWhenVisible } from "./modules/observer";

(function() {
    const CIRCLE_ANIMATION_LENGTH = 1500;
    const ANIMATION_SPEED = 20;

    const loadMenuScroll = () => {
        const menu = document.getElementById('navigation');
        if(!menu) return;
        
        let scrollPosition = 0;

        const checkScroll = () => {
            scrollPosition = window.scrollY;

            if(scrollPosition !== 0) {
                menu.classList.add('bg-white/30', 'py-10!', 'backdrop-blur-sm', 'shadow-card', 'extended');
            } else {
                menu.classList.remove('bg-white/30', 'py-10!', 'backdrop-blur-sm', 'shadow-card', 'extended');
            }
        }

        window.addEventListener('scroll', () => {
            checkScroll();
        });
        checkScroll();

        menu.classList.remove('opacity-0');
    }

    const loadFooterAnimation = () => {
        const footer = document.getElementById('footer');
        if(!footer) return;

        const topCurve = footer.querySelector('.top-curve');
        if(!topCurve) return;

        runWhenVisible(footer as HTMLElement, () => {
            topCurve.classList.add('active');
        }, () => {
            topCurve.classList.remove('active');
        }, {
            rootMargin: '0px',
            threshold: 0.1,
        });
    }

    const loadMenu = () => {
        const menuToggle = document.getElementById('menu-toggle');
        if(!menuToggle) return;

        const mobileMenu = document.getElementById('mobile-menu');
        if(!mobileMenu) return;

        const menuClose = document.getElementById('menu-close');
        if(!menuClose) return;

        menuToggle.addEventListener('click', () => {
            mobileMenu.classList.remove('h-0', 'opacity-0');
            mobileMenu.classList.add('h-screen');
        });

        menuClose.addEventListener('click', () => {
            mobileMenu.classList.remove('h-screen');
            mobileMenu.classList.add('h-0', 'opacity-0');
        });
    }

    const loadMobileMenu = () => {
        const expandableMenu = document.getElementById('mobile-menu');
        if(!expandableMenu) return;
        
        const itemWithChildren = expandableMenu.querySelectorAll('.menu-item-has-children');
        for(const item of itemWithChildren) {
            const anchor = item.querySelector('a');
            if(!anchor) continue;

            anchor.addEventListener('click', (e) => {
                if(item.classList.contains('menu-item-has-children')) {
                    e.preventDefault();
                    const subMenu = item.querySelector('.sub-menu') as HTMLElement;
                    if(!subMenu) return;
    
                    item.classList.toggle('open');
                    if(subMenu.style.display === 'flex') {
                        subMenu.style.display = 'none';
                    } else {
                        subMenu.style.display = 'flex';
                    }
                }
            });
        }
    }

    const loadJSAnimations = (key: string) => {
        switch(key) {
            case 'circle':
                loadCircleAnimations();
                break;
        }
    }

    const loadAnimations = () => {
        const animations = document.querySelectorAll('.animation') as NodeListOf<HTMLElement>;
        for(const animation of animations) {
            let animationName = '';
            const threshold = Number(animation.getAttribute('data-animation-threshold') ?? 0);

            animation.classList.forEach(className => {
                if(className.startsWith('animation-')) {
                    animationName = className;
                }
            });

            if(!animationName) continue;

            animationName = animationName.replace('animation-', '');

            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add(animationName);
                        loadJSAnimations(animationName);
                    } else {
                        // Only remove class if element is below viewport
                        const rect = entry.target.getBoundingClientRect();
                        if (rect.y > 0) {
                            entry.target.classList.remove(animationName);
                        }
                    }
                });
            }, {
                threshold
            });

            observer.observe(animation);
        }
    };

    const loadCircleAnimations = () => {
        const circleContainers = document.querySelectorAll('.circle-container') as NodeListOf<HTMLElement>;

        for(const container of circleContainers) {
            const animationType = container.getAttribute('data-animation') ?? 'stroke';
            const circle = container.querySelector('.circle') as HTMLElement;
            
            if (animationType === 'opacity') {
                // Opacity animation: fade from 0 to 1
                const fillPercent = Number(circle.getAttribute('data-fill-percent') ?? 100);
                const text = container.querySelector('.circle-text') as HTMLElement;
                let i = 1;
                circle.style.opacity = '0';

                const animate = () => {
                    const opacity = i / CIRCLE_ANIMATION_LENGTH;
                    const percent = Math.ceil(fillPercent * i / CIRCLE_ANIMATION_LENGTH);
                    
                    circle.style.opacity = opacity.toFixed(3);
                    text.innerText = `${percent.toFixed(0)}%`;
                    
                    i += ANIMATION_SPEED;
                    if (i < CIRCLE_ANIMATION_LENGTH) {
                        requestAnimationFrame(animate);
                    } else {
                        circle.style.opacity = '1';
                        text.innerText = `${fillPercent.toFixed(0)}%`;
                    }
                };

                requestAnimationFrame(animate);
            } else {
                // Stroke-dasharray animation (default)
                const fillPercent = Number(circle.getAttribute('data-fill-percent') ?? 100);
                const fg = circle.querySelector('.fg') as SVGCircleElement;
                const radius = Number(fg.getAttribute('r') ?? 1);
                const circumference = 2 * Math.PI * radius;
                const text = container.querySelector('.circle-text') as HTMLElement;

                let i = 1;

                const animate = () => {
                    const filledPart = 2 * Math.PI * radius * (i / CIRCLE_ANIMATION_LENGTH) * (fillPercent / 100);
                    const sparePart = circumference - filledPart;
                    const percent = Math.ceil(fillPercent * i / CIRCLE_ANIMATION_LENGTH);

                    fg.setAttribute('stroke-dasharray', `${filledPart.toFixed(2)} ${sparePart.toFixed(2)}`);
                    text.innerText = `${percent.toFixed(0)}%`;
                    
                    i += ANIMATION_SPEED;
                    if (i < CIRCLE_ANIMATION_LENGTH) {
                        requestAnimationFrame(animate);
                    } else {
                        text.innerText = `${fillPercent.toFixed(0)}%`;
                    }
                };

                requestAnimationFrame(animate);
            }
        }
    }

    const loadPrefetch = () => {
        if (!HTMLScriptElement.supports || !HTMLScriptElement.supports('speculationrules')) {
            const preloadedUrls: Record<string, boolean> = {};
        
            function pointerenterHandler (el: HTMLAnchorElement) {
              if (!preloadedUrls[el.href]) {
                preloadedUrls[el.href] = true;
        
                const prefetcher = document.createElement('link');
        
                prefetcher.as = prefetcher.relList.supports('prefetch') ? 'document' : 'fetch';
                prefetcher.rel = prefetcher.relList.supports('prefetch') ? 'prefetch' : 'preload';
                prefetcher.href = el.href;
        
                document.head.appendChild(prefetcher);
                console.log(prefetcher);
              }
            }
        
            document.querySelectorAll('a[href]').forEach(item => {
              item.addEventListener('pointerenter', () => pointerenterHandler(item as HTMLAnchorElement));
            });
        }
    }

    window.addEventListener('load', () => {
        loadMenuScroll();
        loadFooterAnimation();
        loadMenu();
        loadMobileMenu();
        loadAnimations();
        loadPrefetch();
    });
})();